import {
  Flex,
  Grid,
  Text,
  useMediaQuery,
  Table,
  Button,
  Image,
  Spinner,
} from "@chakra-ui/react";
import { PRIMARY_COLOR } from "../../constants/colors";
import { FaWhatsapp } from "react-icons/fa";
import { BsCart2 } from "react-icons/bs";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import HeaderTools from "../../components/HeaderTools";
import api from "../../services/api";
import { toast } from "react-toastify";

const normalizeImageUrl = (path) => {
  if (!path) return "";
  const normalized = path.replace(/\\/g, "/");
  const index = normalized.indexOf("/uploads");
  if (index === -1) return "";
  return `https://api.aqvex.com.br${normalized.substring(index)}`;
};

const formatPhone = (value) => {
  if (!value) return "-";
  const digits = value.toString().replace(/\D/g, "");
  if (digits.length === 11) {
    return digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  }
  if (digits.length === 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  }
  return digits.replace(/(\d{2})(\d{0,5})(\d{0,4})/, "($1) $2-$3").trim();
};

export default function CartAbandonedDetail() {
  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [cartData, setCartData] = useState(null);
  const [productsData, setProductsData] = useState({});

  useEffect(() => {
    const getAbandonedCart = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/cart/abandoned/${id}`);
        setCartData(response.data);
      } catch {
        toast.error("Não foi possível carregar este carrinho.");
      } finally {
        setLoading(false);
      }
    };

    getAbandonedCart();
  }, [id]);

  const cart = cartData?.products ?? [];

  const totalProductItems = cart.reduce((sum, item) => {
    const totalQuantity = Object.values(item.sizes || {}).reduce(
      (total, size) => total + (size.quantity || 0),
      0,
    );
    return sum + totalQuantity;
  }, 0);

  const groupedProducts = useMemo(() => {
    return Object.values(
      cart.reduce((acc, item) => {
        if (!acc[item.id]) {
          acc[item.id] = {
            ...item,
            colors: [item.color],
            sizes: item.sizes,
          };
        } else {
          if (!acc[item.id].colors.includes(item.color)) {
            acc[item.id].colors.push(item.color);
          }
        }
        return acc;
      }, {}),
    );
  }, [cart]);

  const productIds = useMemo(
    () => Array.from(new Set(groupedProducts.map((p) => p.id))),
    [groupedProducts],
  );

  useEffect(() => {
    const loadAllProducts = async () => {
      if (!cartData?.companyHash || productIds.length === 0) {
        setProductsData({});
        return;
      }
      try {
        const responses = await Promise.all(
          productIds.map((id) =>
            api.get(
              `/client-database/product-variations/${id}/${cartData.companyHash}`,
            ),
          ),
        );

        const formatted = {};
        responses.forEach((res, idx) => {
          const base = res.data.data || [];
          const id = productIds[idx];
          formatted[id] = {
            variations: base,
          };
        });

        setProductsData(formatted);
      } catch {}
    };

    loadAllProducts();
  }, [cartData?.companyHash, productIds]);

  const getQuantity = (productId, color, size) => {
    const item = cart.find((i) => i.id === productId && i.color === color);
    return item?.sizes?.[size]?.quantity ?? 0;
  };

  const getColorThumbnail = (variations, color) => {
    const variation = variations.find((v) => v.cor === color);
    return normalizeImageUrl(variation?.url_imagem_variacao);
  };

  const getMainImage = (variations) => {
    return normalizeImageUrl(variations?.[0]?.url_imagem_principal);
  };

  if (loading) {
    return (
      <Flex width="100%" justifyContent="center" paddingTop="150px">
        <Spinner />
      </Flex>
    );
  }

  return (
    <>
      <HeaderTools title="Carrinho Abandonado" />
      <Flex
        width="100%"
        padding="15px"
        justifyContent="center"
        minH="100vh"
        paddingTop={isLargerThan768 ? "100px" : "120px"}
      >
        <Flex maxW={isLargerThan768 ? "1200px" : "100%"} width="100%">
          {cart.length === 0 && (
            <Flex
              flexDirection="column"
              width="100%"
              justifyContent="center"
              alignItems="center"
              gap="20px"
            >
              <BsCart2 size={60} />
              <Text>Este carrinho não possui mais itens.</Text>
              <Button
                backgroundColor={PRIMARY_COLOR}
                padding="10px 15px"
                onClick={() => navigate(-1)}
              >
                Voltar
              </Button>
            </Flex>
          )}

          {cart.length > 0 && (
            <Grid
              templateColumns={isLargerThan768 ? "1fr 0.5fr" : "1fr"}
              columnGap={isLargerThan768 ? "80px" : "0px"}
              width="100%"
            >
              <Flex flexDirection="column" gap="30px" flex={1}>
                <Flex flexDirection="column" gap="4px" paddingBottom="10px">
                  <Text fontWeight="700" fontSize="18px">
                    {cartData?.client?.name}
                  </Text>
                  <Text color="gray.600">{cartData?.client?.email}</Text>
                  <Flex alignItems="center" gap="5px" color="gray.600">
                    <FaWhatsapp />
                    <Text>{formatPhone(cartData?.client?.phone)}</Text>
                  </Flex>
                  <Text color="gray.600">{cartData?.client?.cpf_cnpj}</Text>
                </Flex>

                {groupedProducts.map((product) => {
                  const variations = productsData[product.id]?.variations ?? [];

                  const totalItemsByProduct = cart
                    .filter((item) => item.id === product.id)
                    .reduce(
                      (sum, item) =>
                        sum +
                        Object.values(item.sizes || {}).reduce(
                          (s, size) => s + (size.quantity || 0),
                          0,
                        ),
                      0,
                    );

                  const sizesFromVariations = [
                    ...new Set(variations.map((v) => v.tam).filter(Boolean)),
                  ];

                  const sizesToRender =
                    sizesFromVariations.length > 0
                      ? sizesFromVariations
                      : Object.keys(product.sizes ?? {});

                  const colorsInCart = product.colors ?? [];

                  const mainImage = getMainImage(variations);

                  return (
                    <Flex
                      key={product.id}
                      flexDirection="column"
                      paddingBottom="20px"
                      gap="10px"
                    >
                      <Flex align="center" gap="10px">
                        {mainImage && (
                          <Image
                            src={mainImage}
                            boxSize="120px"
                            objectFit="cover"
                          />
                        )}
                        <div>
                          <Text fontSize="20px" fontWeight={700}>
                            {product.name}
                          </Text>
                          <Text fontSize="14px" color="gray.500">
                            Referência: {product.prod_referencia}
                          </Text>
                          <Text
                            fontWeight={600}
                            fontSize="18px"
                            color="green.600"
                          >
                            {Intl.NumberFormat("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            }).format(product.price)}
                          </Text>
                        </div>
                      </Flex>

                      <Table.Root size="lg" showColumnBorder>
                        <Table.Header>
                          <Table.Row backgroundColor="white">
                            <Table.ColumnHeader></Table.ColumnHeader>
                            {sizesToRender.map((size) => (
                              <Table.ColumnHeader
                                key={size}
                                padding="15px"
                                textAlign="center"
                              >
                                {size}
                              </Table.ColumnHeader>
                            ))}
                          </Table.Row>
                        </Table.Header>

                        <Table.Body>
                          {colorsInCart.map((color) => {
                            const thumbnail = getColorThumbnail(
                              variations,
                              color,
                            );
                            const colorHex =
                              variations.find((v) => v.cor === color)
                                ?.cor_hexadecimal ?? color;

                            return (
                              <Table.Row key={color} minH="60px">
                                <Table.Cell padding="15px" maxW="80px">
                                  <Flex
                                    w="40px"
                                    h="40px"
                                    borderRadius="50%"
                                    bg={thumbnail ? undefined : colorHex}
                                    bgImage={
                                      thumbnail ? `url(${thumbnail})` : undefined
                                    }
                                    bgSize="cover"
                                    bgPosition="center"
                                    bgRepeat="no-repeat"
                                    border="1px solid #ccc"
                                  />
                                  <Text fontSize="12px" mt="5px">
                                    {color}
                                  </Text>
                                </Table.Cell>

                                {sizesToRender.map((size) => {
                                  const quantity = getQuantity(
                                    product.id,
                                    color,
                                    size,
                                  );

                                  return (
                                    <Table.Cell
                                      key={size}
                                      padding="15px"
                                      textAlign="center"
                                    >
                                      <Flex
                                        align="center"
                                        justify="center"
                                        minH="60px"
                                      >
                                        <Text fontSize="sm">
                                          {quantity > 0 ? quantity : "-"}
                                        </Text>
                                      </Flex>
                                    </Table.Cell>
                                  );
                                })}
                              </Table.Row>
                            );
                          })}
                        </Table.Body>
                      </Table.Root>

                      <Flex>
                        <Text fontSize="14px" fontWeight="600" color="gray.600">
                          {totalItemsByProduct}{" "}
                          {totalItemsByProduct > 1 ? "pçs" : "pç"}
                        </Text>
                      </Flex>
                    </Flex>
                  );
                })}
              </Flex>

              <Flex flexDirection="column" gap="20px">
                <Text fontWeight="600">Vendedor</Text>
                <Flex gap="10px">
                  <Flex
                    width="50px"
                    height="50px"
                    alignItems="center"
                    justifyContent="center"
                    backgroundColor="gray"
                    color="white"
                    borderRadius="50%"
                  >
                    {cartData?.sellerName?.[0] ?? "-"}
                  </Flex>
                  <Flex flexDirection="column">
                    <Text fontWeight="600">{cartData?.sellerName ?? "-"}</Text>
                    <Flex alignItems="center" gap="5px">
                      <FaWhatsapp />
                      <Text>{formatPhone(cartData?.sellerCellphone)}</Text>
                    </Flex>
                  </Flex>
                </Flex>
                <Flex
                  padding="15px"
                  backgroundColor="rgb(244 244 244)"
                  flexDirection="column"
                  gap="10px"
                >
                  <Flex justifyContent="space-between">
                    <Text>Solicitado</Text>
                    <Flex alignItems="center" gap="10px">
                      <Text>
                        {totalProductItems}{" "}
                        {totalProductItems > 1 ? "pçs" : "pç"}
                      </Text>
                      <Text>
                        {Intl.NumberFormat("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        }).format(cartData?.totalValue ?? 0)}
                      </Text>
                    </Flex>
                  </Flex>
                  <hr />
                  <Flex justifyContent="space-between">
                    <Text fontWeight="700">Total</Text>
                    <Text fontWeight="600">
                      {Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(cartData?.totalValue ?? 0)}
                    </Text>
                  </Flex>
                </Flex>
                <Text fontSize="13px" color="gray.500">
                  Atualizado em{" "}
                  {cartData?.updatedAt &&
                    new Intl.DateTimeFormat("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    }).format(new Date(cartData.updatedAt))}
                </Text>
                <Button
                  variant="outline"
                  onClick={() => navigate(-1)}
                  padding="10px"
                >
                  <BsCart2 />
                  Voltar
                </Button>
              </Flex>
            </Grid>
          )}
        </Flex>
      </Flex>
    </>
  );
}
